import React, { useMemo } from 'react'
import { Paper } from '@mui/material'
import { DataGrid, GridRowsProp, frFR, enUS } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import AnalysisEmptyState from './AnalysisEmptyState'
import TablePager from './TablePager'
import { useClientPagination } from '../../hooks/useClientPagination'
import { listPageGridSlots } from './ListPageShell'

interface MatrixProps {
  title: string
  data: Array<{ [key: string]: string | number | React.ReactNode }>
  headers: string[]
  /** Compact branded table for analysis pages */
  variant?: 'default' | 'analysis'
  /** Max height of the scroll area (analysis variant). Default ~480px */
  maxHeight?: number | string
  /** Client-side pagination for analysis tables (default true). */
  paginate?: boolean
  pageSize?: number
}

function isNumericHeader(header: string) {
  const h = header.toLowerCase()
  return (
    /budget|cost|coût|cout|amount|montant|total|score|count|ratio|%|€|\$/.test(
      h
    ) || /\b(price|prix)\b/.test(h)
  )
}

function looksNumeric(value: unknown) {
  if (typeof value === 'number') return true
  if (typeof value !== 'string') return false
  const cleaned = value.replace(/[\s€$£,]/g, '').replace(/\./g, '')
  return cleaned !== '' && /^-?\d+(\.\d+)?%?$/.test(cleaned.replace(',', '.'))
}

function isRichCell(value: unknown) {
  return React.isValidElement(value)
}

function looksLongText(value: unknown) {
  return typeof value === 'string' && value.length > 48
}

const MatrixComponent: React.FC<MatrixProps> = ({
  title,
  data,
  headers,
  variant = 'default',
  maxHeight,
  paginate = true,
  pageSize = 10,
}) => {
  const { t, i18n } = useTranslation()
  const pager = useClientPagination(data, pageSize)
  const visibleData = paginate && variant === 'analysis' ? pager.pageItems : data

  const numericCols = useMemo(() => {
    const set = new Set<string>()
    headers.forEach((header, idx) => {
      if (isNumericHeader(header)) {
        set.add(header)
        return
      }
      const sample = data.slice(0, 8).map((row) => row[header])
      if (sample.some(isRichCell)) return
      const hits = sample.filter(looksNumeric).length
      if (sample.length > 0 && hits / sample.length >= 0.6 && idx > 0) {
        set.add(header)
      }
    })
    return set
  }, [headers, data])

  const wrapCols = useMemo(() => {
    const set = new Set<string>()
    headers.forEach((header, idx) => {
      if (idx === 0 || numericCols.has(header)) return
      const sample = data.slice(0, 8).map((row) => row[header])
      if (sample.some((v) => isRichCell(v) || looksLongText(v))) {
        set.add(header)
      }
    })
    return set
  }, [headers, data, numericCols])

  if (variant === 'analysis') {
    const scrollStyle = maxHeight
      ? {
          maxHeight:
            typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
        }
      : undefined

    return (
      <div className="ma-analysis-table-wrap">
        {data.length === 0 ? (
          <AnalysisEmptyState
            title={t('charts.noData', { defaultValue: 'Nothing to show yet' })}
            hint={t('charts.noDataHint', {
              defaultValue:
                'This indicator will appear once related records exist.',
            })}
          />
        ) : (
          <div className="ma-analysis-table-scroll" style={scrollStyle}>
            <table className="ma-analysis-table" aria-label={title}>
              <thead>
                <tr>
                  {headers.map((header, colIdx) => (
                    <th
                      key={header}
                      className={
                        [
                          numericCols.has(header) ? 'is-numeric' : '',
                          colIdx === 0 ? 'is-sticky' : '',
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                      scope="col"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleData.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {headers.map((header, colIdx) => {
                      const value = row[header] ?? '—'
                      const className = [
                        numericCols.has(header) ? 'is-numeric' : '',
                        colIdx === 0 ? 'is-primary is-sticky' : '',
                        wrapCols.has(header) ? 'is-wrap' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')
                      return (
                        <td key={header} className={className || undefined}>
                          {value}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.length > 0 && paginate && pager.showPager ? (
          <TablePager
            page={pager.page}
            pageSize={pager.pageSize}
            total={pager.total}
            pageCount={pager.pageCount}
            from={pager.from}
            to={pager.to}
            pageSizeOptions={pager.pageSizeOptions}
            onPageChange={pager.setPage}
            onPageSizeChange={pager.setPageSize}
          />
        ) : data.length > 0 ? (
          <div className="ma-analysis-table-foot">
            <span>
              {t('analysis.table.rowCount', {
                count: data.length,
                defaultValue:
                  data.length === 1 ? '1 row' : `${data.length} rows`,
              })}
            </span>
          </div>
        ) : null}
      </div>
    )
  }

  const columns: any[] = headers.map((header) => ({
    field: header,
    headerName: header,
    minWidth: 200,
    flex: 2,
    sortable: true,
    align: numericCols.has(header) ? 'right' : 'left',
    headerAlign: numericCols.has(header) ? 'right' : 'left',
    renderCell: (params: any) => (
      <div
        style={{
          whiteSpace: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          maxHeight: '80px',
          overflowY: params.field === 'ExistingControls' ? 'auto' : 'hidden',
          textAlign: numericCols.has(header) ? 'right' : 'left',
          width: '100%',
        }}
      >
        {params.value}
      </div>
    ),
  }))

  const currentLocaleText =
    i18n.language === 'fr'
      ? frFR.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText

  const rows: GridRowsProp = data.map((row, index) => ({
    id: index,
    ...row,
  }))

  return (
    <Paper
      style={{
        padding: 0,
        height: '100%',
        marginTop: '20px',
        marginBottom: '20px',
        boxShadow: 'none',
      }}
    >
      <div className="w-full max-w-full overflow-hidden" style={{ height: 400 }}>
        <DataGrid
          slots={listPageGridSlots}
          rows={rows}
          localeText={currentLocaleText}
          key={i18n.language}
          className="dataGridCustomStyles"
          columns={columns}
          disableColumnSelector={false}
          hideFooterPagination={false}
          getRowId={(row) => row.id}
          sx={{
            '.MuiDataGrid-cell': {
              overflow: 'visible',
              whiteSpace: 'normal !important',
            },
          }}
        />
      </div>
    </Paper>
  )
}

export default MatrixComponent
